import React, { useState, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { BUNDLE_EASE } from "../../utils/variables/common"
import { parseDate } from "../../utils/parsers/common"
import { getFromDate } from "../../utils/parsers/orders.js"
import { Trans, useTranslation } from "react-i18next"
import { OverwriteConfirmModal } from "./"

const PROVIDERS_PER_PAGE = 4
const MAX_VISIBLE_DOTS = 7

const isMissingCredentials = () => false

const getVisiblePages = (current, total) => {
  if (total <= MAX_VISIBLE_DOTS) {
    return Array.from({ length: total }, (_, i) => i)
  }
  const half = Math.floor((MAX_VISIBLE_DOTS - 2) / 2)
  let start = Math.max(1, current - half)
  let end = Math.min(total - 2, current + half)

  if (end - start < MAX_VISIBLE_DOTS - 3) {
    if (start === 1) end = Math.min(total - 2, start + MAX_VISIBLE_DOTS - 3)
    if (end === total - 2) start = Math.max(1, end - (MAX_VISIBLE_DOTS - 3))
  }

  const pages = [0]
  if (start > 1) pages.push("left-ellipsis")
  for (let i = start; i <= end; i++) pages.push(i)
  if (end < total - 2) pages.push("right-ellipsis")
  pages.push(total - 1)
  return pages
}

const QueryStep = ({ onResults, providers = [], providerOrders = {} }) => {
  const { t } = useTranslation()

  const [providerId, setProviderId] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [slideDir, setSlideDir] = useState(1)
  const [selectedSysNames, setSelectedSysNames] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [filtersOpen, setFiltersOpen] = useState(false)

  const allSysNames = useMemo(() => {
    const map = {}
    providers.forEach((p) => {
      if (p.sys_name) map[p.sys_name] = (map[p.sys_name] || 0) + 1
    })
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [providers])

  const filteredProviders = useMemo(() => {
    let result = providers
    if (selectedSysNames.length > 0) {
      result = result.filter((p) => selectedSysNames.includes(p.sys_name))
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      result = result.filter(
        (p) => p.company_name?.toLowerCase().includes(q) || p.sys_name?.toLowerCase().includes(q)
      )
    }
    return result
  }, [providers, selectedSysNames, searchQuery])

  const totalPages = Math.ceil(filteredProviders.length / PROVIDERS_PER_PAGE)
  const pagedProviders = filteredProviders.slice(
    page * PROVIDERS_PER_PAGE,
    (page + 1) * PROVIDERS_PER_PAGE
  )

  const selectedProvider = providers.find((p) => p.id === providerId)
  const providerMissingCreds = isMissingCredentials(selectedProvider)

  const canSearch = providerId && dateFrom && dateTo && !providerMissingCreds

  const dateRangeDays =
    dateFrom && dateTo
      ? Math.round((new Date(dateTo) - new Date(dateFrom)) / (1000 * 60 * 60 * 24))
      : 0
  const isWideRange = dateRangeDays > 2

  const handleSearch = async () => {
    if (!canSearch) return
    setLoading(true)
    await new Promise((r) => setTimeout(r, 800))
    const from = new Date(dateFrom)
    const to = new Date(dateTo + "T23:59:59")
    const result = (providerOrders[providerId] || []).filter((o) => {
      const d = parseDate(getFromDate(o))
      return d ? d >= from && d <= to : true
    })
    setLoading(false)
    onResults({ providerId, dateFrom, dateTo, orders: result })
  }

  const toggleSysName = (name) => {
    setSelectedSysNames((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    )
    setPage(0)
  }

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
    setPage(0)
  }

  const selectedOnCurrentPage = pagedProviders.some((p) => p.id === providerId)

  const providerCardHeight = 64
  const providerGap = 8
  const listHeight =
    PROVIDERS_PER_PAGE * providerCardHeight + (PROVIDERS_PER_PAGE - 1) * providerGap

  const visiblePages = getVisiblePages(page, totalPages)

  const goToPage = (i) => {
    setSlideDir(i > page ? 1 : -1)
    setPage(i)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.3, ease: BUNDLE_EASE }}
      className="flex-1 flex items-start justify-center px-0 sm:px-4 py-0 sm:py-10 bg-gray-100 min-h-0"
    >
      <div className="w-full max-w-lg">
        <motion.div
          className="bg-white sm:rounded-2xl shadow-sm sm:border border-gray-200 overflow-hidden"
          initial={{ opacity: 0, scale: 0.98, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.32, ease: BUNDLE_EASE }}
        >
          <div className="px-4 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-base leading-tight">
                  {t("admin.receive_requests_title")}
                </h2>
                <p className="text-xs text-gray-400">{t("admin.receive_requests_instruction")}</p>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-5">
            <div>
              {/* <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold mr-1.5">
                    1
                  </span>
                  {t("admin.provider_label")}
                </label>
                {providers.length > PROVIDERS_PER_PAGE && (
                  <span className="text-[10px] text-gray-400">
                    <Trans
                      i18nKey="admin.provider_count_label"
                      values={{ count: providers.length }}
                    />
                  </span>
                )}
              </div> */}
              {/* 
              {allSysNames.length > 1 && (
                <div className="mb-3">
                  <div className="flex sm:hidden items-center gap-2 mb-0">
                    <motion.button
                      onClick={() => setFiltersOpen((v) => !v)}
                      whileTap={{ scale: 0.96 }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                        filtersOpen || selectedSysNames.length > 0
                          ? "bg-blue-50 border-blue-300 text-blue-700"
                          : "bg-white border-gray-200 text-gray-500"
                      }`}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                      </svg>
                      Фільтр
                      {selectedSysNames.length > 0 && (
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] font-bold">
                          {selectedSysNames.length}
                        </span>
                      )}
                      <motion.svg
                        className="w-3 h-3 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        animate={{ rotate: filtersOpen ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </motion.svg>
                    </motion.button>

                    {selectedSysNames.length > 0 && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.85 }}
                        onClick={() => { setSelectedSysNames([]); setPage(0) }}
                        className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {t("admin.reset")}
                      </motion.button>
                    )}
                  </div>

                  <AnimatePresence>
                    {filtersOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: "auto", marginTop: 8 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden flex sm:hidden flex-wrap gap-1.5"
                      >
                        {allSysNames.map(({ name, count }) => {
                          const active = selectedSysNames.includes(name)
                          return (
                            <motion.button
                              key={name}
                              onClick={() => toggleSysName(name)}
                              whileTap={{ scale: 0.93 }}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-mono font-semibold transition-colors ${
                                active
                                  ? "bg-blue-600 border-blue-600 text-white"
                                  : "bg-white border-gray-200 text-gray-500"
                              }`}
                            >
                              {name}
                              <span className={`text-[9px] font-sans px-1 py-0.5 rounded ${active ? "bg-blue-500 text-blue-100" : "bg-gray-100 text-gray-400"}`}>
                                {count}
                              </span>
                            </motion.button>
                          )
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="hidden sm:flex flex-wrap gap-1.5">
                    {allSysNames.map(({ name, count }) => {
                      const active = selectedSysNames.includes(name)
                      return (
                        <motion.button
                          key={name}
                          onClick={() => toggleSysName(name)}
                          whileTap={{ scale: 0.93 }}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-mono font-semibold transition-colors ${
                            active
                              ? "bg-blue-600 border-blue-600 text-white"
                              : "bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700"
                          }`}
                        >
                          {name}
                          <span className={`text-[9px] font-sans px-1 py-0.5 rounded ${active ? "bg-blue-500 text-blue-100" : "bg-gray-100 text-gray-400"}`}>
                            {count}
                          </span>
                        </motion.button>
                      )
                    })}
                    <AnimatePresence>
                      {selectedSysNames.length > 0 && (
                        <motion.button
                          initial={{ opacity: 0, scale: 0.85 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.85 }}
                          transition={{ duration: 0.15 }}
                          onClick={() => { setSelectedSysNames([]); setPage(0) }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          {t("admin.reset")}
                        </motion.button>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )} */}

              <div className="relative mb-3">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t("admin.search_provider_placeholder", {
                    defaultValue: "Пошук провайдера...",
                  })}
                  className="w-full border-2 border-gray-200 rounded-xl py-2 pl-8 pr-8 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors bg-white"
                />
                <AnimatePresence>
                  {searchQuery && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.12 }}
                      onClick={() => {
                        setSearchQuery("")
                        setPage(0)
                      }}
                      className="absolute right-2.5 inset-y-0 my-auto w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                    >
                      <svg
                        className="w-2.5 h-2.5 text-gray-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>

              <AnimatePresence>
                {selectedProvider && !selectedOnCurrentPage && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                    animate={{ opacity: 1, height: "auto", marginBottom: 8 }}
                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 border border-blue-200">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                      <span className="text-xs text-blue-700 font-medium flex-1 truncate">
                        {t("admin.selected2")}: {selectedProvider.company_name}
                      </span>
                      <button
                        onClick={() => {
                          setSelectedSysNames([])
                          const idx = providers.findIndex((p) => p.id === providerId)
                          setPage(Math.floor(idx / PROVIDERS_PER_PAGE))
                        }}
                        className="text-[10px] text-blue-500 hover:text-blue-700 font-semibold shrink-0 transition-colors"
                      >
                        {t("admin.go_to_lable")} →
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="relative" style={{ height: `${listHeight}px` }}>
                <div
                  className="absolute inset-0"
                  style={{ overflow: "hidden", margin: "-4px -8px", padding: "4px 8px" }}
                >
                  <AnimatePresence initial={false} custom={slideDir}>
                    {filteredProviders.length === 0 ? (
                      <motion.div
                        key="empty"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 flex flex-col items-center justify-center gap-2"
                      >
                        <svg
                          className="w-8 h-8 text-gray-300"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <p className="text-xs text-gray-400">{t("admin.no_providers_found")}</p>
                      </motion.div>
                    ) : (
                      <motion.div
                        key={`${page}-${selectedSysNames.join(",")}`}
                        custom={slideDir}
                        variants={{
                          enter: (dir) => ({ x: dir * 40, opacity: 0 }),
                          center: { x: 0, opacity: 1 },
                          exit: (dir) => ({ x: dir * -40, opacity: 0 }),
                        }}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                        className="absolute inset-0 flex flex-col gap-2"
                      >
                        {pagedProviders.map((p, i) => {
                          const sel = providerId === p.id
                          const missingCreds = isMissingCredentials(p)
                          return (
                            <motion.button
                              key={p.id}
                              onClick={() => setProviderId(p.id)}
                              className={`w-full flex items-center gap-3 px-4 py-3 sm:py-3 rounded-xl border-2 text-left transition-colors h-16 shrink-0 active:scale-[0.99] ${
                                sel
                                  ? missingCreds
                                    ? "border-amber-400 bg-amber-50"
                                    : "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-gray-300 bg-white"
                              }`}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: i * 0.04, duration: 0.18 }}
                              whileHover={{ x: 2, boxShadow: "0 2px 8px 0 rgba(0,0,0,0.08)" }}
                              style={{ transformOrigin: "center" }}
                              whileTap={{ x: 0, boxShadow: "none" }}
                            >
                              <div className="flex-1 min-w-0">
                                <div
                                  className={`text-sm font-semibold leading-tight ${sel ? (missingCreds ? "text-amber-700" : "text-blue-700") : "text-gray-800"}`}
                                >
                                  {p.company_name}
                                </div>
                                {/* <div className="text-xs text-gray-400 mt-0.5 font-mono">
                                  {p.sys_name}
                                </div> */}
                              </div>

                              {missingCreds && (
                                <div className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-50 border border-amber-200">
                                  <svg
                                    className="w-3 h-3 text-amber-500"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                                    />
                                  </svg>
                                  <span className="text-[9px] font-semibold text-amber-600 leading-none">
                                    {!p.site_host && !p.password
                                      ? "host / pass"
                                      : !p.site_host
                                        ? "host"
                                        : "pass"}
                                  </span>
                                </div>
                              )}

                              <motion.div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                  sel
                                    ? missingCreds
                                      ? "border-amber-400 bg-amber-400"
                                      : "border-blue-500 bg-blue-500"
                                    : "border-gray-300"
                                }`}
                                animate={{ scale: sel ? [1, 1.3, 1] : 1 }}
                                transition={{ duration: 0.25 }}
                              >
                                {sel && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                              </motion.div>
                            </motion.button>
                          )
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3 gap-2">
                  <motion.button
                    onClick={() => {
                      setSlideDir(-1)
                      setPage((p) => Math.max(0, p - 1))
                    }}
                    disabled={page === 0}
                    className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-semibold transition-colors shrink-0 min-w-[64px] ${
                      page === 0
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:text-gray-800 hover:bg-gray-100 active:bg-gray-100"
                    }`}
                    whileTap={page > 0 ? { scale: 0.94 } : {}}
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    {t("admin.back")}
                  </motion.button>

                  <span className="flex sm:hidden text-xs font-semibold text-gray-500 px-1 shrink-0">
                    {page + 1}
                    <span className="text-gray-300 mx-0.5">/</span>
                    {totalPages}
                  </span>

                  <div className="hidden sm:flex items-center gap-1 flex-1 justify-center overflow-hidden">
                    {visiblePages.map((item, idx) => {
                      if (item === "left-ellipsis" || item === "right-ellipsis") {
                        return (
                          <span
                            key={`${item}-${idx}`}
                            className="text-gray-300 text-xs leading-none px-0.5 select-none"
                          >
                            ···
                          </span>
                        )
                      }

                      const i = item
                      const isActive = i === page
                      const hasSelected =
                        selectedProvider &&
                        (() => {
                          const idx2 = filteredProviders.findIndex((p) => p.id === providerId)
                          return (
                            idx2 >= i * PROVIDERS_PER_PAGE && idx2 < (i + 1) * PROVIDERS_PER_PAGE
                          )
                        })()

                      return (
                        <motion.button
                          key={i}
                          onClick={() => goToPage(i)}
                          className="flex items-center justify-center h-6 px-0.5 shrink-0"
                          whileTap={{ scale: 0.85 }}
                          title={`Сторінка ${i + 1}`}
                        >
                          <motion.div
                            animate={{
                              width: isActive ? 20 : 6,
                              backgroundColor: isActive
                                ? "#2563eb"
                                : hasSelected
                                  ? "#93c5fd"
                                  : "#d1d5db",
                            }}
                            transition={{ duration: 0.2 }}
                            className="h-1.5 rounded-full"
                          />
                        </motion.button>
                      )
                    })}
                  </div>

                  <motion.button
                    onClick={() => {
                      setSlideDir(1)
                      setPage((p) => Math.min(totalPages - 1, p + 1))
                    }}
                    disabled={page === totalPages - 1}
                    className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-semibold transition-colors shrink-0 min-w-[64px] justify-end ${
                      page === totalPages - 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:text-gray-800 hover:bg-gray-100 active:bg-gray-100"
                    }`}
                    whileTap={page < totalPages - 1 ? { scale: 0.94 } : {}}
                  >
                    {t("pagination.next")}
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </motion.button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold mr-1.5">
                  2
                </span>
                {t("admin.receive_range_date_label")}
              </label>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex-1">
                  <label className="text-[10px] text-gray-400 mb-1 block">
                    {t("admin.date_from_label")}
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl py-2.5 px-3 text-sm text-gray-800 font-medium focus:outline-none focus:border-blue-500 transition-colors bg-white min-w-0"
                  />
                </div>
                <div className="pt-4 text-gray-400 shrink-0 text-sm">—</div>
                <div className="flex-1">
                  <label className="text-[10px] text-gray-400 mb-1 block">
                    {t("admin.date_to_label")}
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    min={dateFrom || undefined}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl py-2.5 px-3 text-sm text-gray-800 font-medium focus:outline-none focus:border-blue-500 transition-colors bg-white min-w-0"
                  />
                </div>
              </div>

              <AnimatePresence>
                {(isWideRange || providerMissingCreds) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: "auto", marginTop: 8 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden flex flex-col gap-2"
                  >
                    {isWideRange && (
                      <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                        <svg
                          className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                          />
                        </svg>
                        <p className="text-xs text-amber-700">
                          <Trans
                            i18nKey="sync.wide_range_warning"
                            values={{ days: dateRangeDays }}
                          />
                        </p>
                      </div>
                    )}

                    {providerMissingCreds && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl">
                          <p className="text-xs text-red-700">
                            <Trans
                              i18nKey="admin.provider_missing_credentials"
                              values={{
                                fields:
                                  !selectedProvider?.site_host && !selectedProvider?.password
                                    ? "site_host, password"
                                    : !selectedProvider?.site_host
                                      ? "site_host"
                                      : "password",
                              }}
                            />
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="px-4 sm:px-6 pb-6 sm:pt-0 sticky bottom-0 bg-white sm:static sm:bg-transparent border-t border-gray-100 sm:border-none pt-4">
            <motion.button
              onClick={handleSearch}
              disabled={!canSearch || loading}
              className={`w-full py-3.5 sm:py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
                canSearch && !loading
                  ? "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-sm"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
              whileHover={canSearch && !loading ? { scale: 1.01 } : {}}
              whileTap={canSearch && !loading ? { scale: 0.97 } : {}}
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  {t("admin.searching_applications")}...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {t("admin.search_applications")}
                </>
              )}
            </motion.button>
            <AnimatePresence>
              {!canSearch && (
                <motion.p
                  className="text-center text-xs text-gray-400 mt-2"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {!providerId ? t("admin.select_provider") : t("admin.select_dates")}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}

export default QueryStep
